// ── SimilarItems.jsx ──────────────────────────────────────────────────────────
// Bottom section: grid of related products (category-similar, any seller)
// ✅ Restyled to match product card design (image, VIDEO badge, heart, name,
//    green price, location pin, star rating)

import React from 'react';
import styles from '../ProductDetailModal.module.css';

const FALLBACK_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='260' " +
  "viewBox='0 0 400 260'%3E%3Crect width='400' height='260' fill='%23f0f0f0'/%3E" +
  "%3Crect x='162' y='85' width='76' height='60' rx='6' fill='%23d0d0d0'/%3E" +
  "%3Ccircle cx='181' cy='103' r='9' fill='%23bbb'/%3E" +
  "%3Cpolygon points='162,145 186,115 206,130 230,105 238,145' fill='%23c8c8c8'/%3E" +
  "%3Ctext x='200' y='185' font-family='Arial' font-size='13' fill='%23aaa' " +
  "text-anchor='middle'%3ENo Image%3C/text%3E%3C/svg%3E";

const VIDEO_EXTS = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
const isVideoUrl = (url) => Boolean(url) && VIDEO_EXTS.some(ext => url.toLowerCase().includes(ext));

const StarRating = ({ rating = 0, count = 0 }) => {
  const stars = Math.round(rating);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '5px' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} width="12" height="12" viewBox="0 0 24 24"
          fill={i <= stars ? '#f59e0b' : '#d1d5db'}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
      {count > 0 && (
        <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '2px' }}>
          ({count} review{count !== 1 ? 's' : ''})
        </span>
      )}
    </div>
  );
};

const SimilarItems = ({ relatedProducts, currentProduct, onProductClick }) => {
  if (!relatedProducts || relatedProducts.length === 0) return null;

  const filtered = relatedProducts.filter(p => p.id !== currentProduct?.id);
  if (filtered.length === 0) return null;

  return (
    <div style={{ padding: '24px 0 8px' }} id="similar-items">

      {/* ── Section header ──────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#111', margin: 0 }}>
          Similar Items
        </h3>
        <a href="#similar-items" style={{ fontSize: '13px', color: '#2563eb', fontWeight: 500, textDecoration: 'none' }}>
          See All →
        </a>
      </div>

      {/* ── Cards grid ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '16px',
      }}>
        {filtered.slice(0, 8).map((p) => {
          const videoUrl  = isVideoUrl(p.img) ? p.img : null;
          const imgSrc    = !videoUrl ? (p.img || FALLBACK_IMG) : null;
          const likeCount = p.like_count ?? p.likes ?? 0;
          const rating    = p.rating ?? p.average_rating ?? 0;
          const ratingCnt = p.rating_count ?? p.reviewCount ?? 0;

          return (
            <div
              key={p.id}
              onClick={() => onProductClick(p)}
              style={{
                background: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
                display: 'flex',
                flexDirection: 'column',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.13)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.10)';
              }}
            >
              {/* ── Image / Video area ───────────────────────────────── */}
              <div style={{ position: 'relative', width: '100%', paddingTop: '68%', background: '#f3f4f6' }}>

                {videoUrl ? (
                  <video
                    src={videoUrl}
                    muted loop playsInline
                    onMouseEnter={e => e.currentTarget.play()}
                    onMouseLeave={e => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                    style={{
                      position: 'absolute', inset: 0,
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <img
                    src={imgSrc}
                    alt={p.name || p.title}
                    onError={e => { if (e.target.src !== FALLBACK_IMG) e.target.src = FALLBACK_IMG; }}
                    style={{
                      position: 'absolute', inset: 0,
                      width: '100%', height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )}

                {/* VIDEO badge — top left */}
                {videoUrl && (
                  <div style={{
                    position: 'absolute', top: 8, left: 8,
                    background: 'rgba(0,0,0,0.70)', color: '#fff',
                    fontSize: '9px', fontWeight: 700,
                    padding: '3px 6px', borderRadius: '5px',
                    display: 'flex', alignItems: 'center', gap: '3px',
                    letterSpacing: '0.5px',
                  }}>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="white">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                    VIDEO
                  </div>
                )}

                {/* Condition badge — top left (if no video) */}
                {!videoUrl && p.condition && (
                  <div style={{
                    position: 'absolute', top: 8, left: 8,
                    background: 'rgba(0,0,0,0.60)', color: '#fff',
                    fontSize: '9px', fontWeight: 700,
                    padding: '3px 7px', borderRadius: '5px',
                    textTransform: 'uppercase', letterSpacing: '0.4px',
                  }}>
                    {p.condition_display || p.condition}
                  </div>
                )}

                {/* Heart / like count — top right */}
                <div style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'rgba(0,0,0,0.55)', color: '#fff',
                  borderRadius: '99px',
                  padding: '4px 8px',
                  fontSize: '11px', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="white">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  {likeCount}
                </div>

                {/* Video play overlay */}
                {videoUrl && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none',
                  }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Card body ────────────────────────────────────────── */}
              <div style={{ padding: '10px 12px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>

                {/* Product name */}
                <div style={{
                  fontSize: '13px', fontWeight: 500, color: '#111',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  marginBottom: '4px',
                }}>
                  {p.name || p.title}
                </div>

                {/* Price — green + bold */}
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#16a34a', marginBottom: '4px' }}>
                  KES {p.price?.toLocaleString()}
                </div>

                {/* Location */}
                {(p.city || p.location) && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    fontSize: '11px', color: '#6b7280', marginBottom: '2px',
                  }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                      stroke="#6b7280" strokeWidth="2.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    {p.city || p.location}
                  </div>
                )}

                {/* Star rating */}
                {rating > 0 && <StarRating rating={rating} count={ratingCnt} />}

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SimilarItems;






























// // ── SimilarItems.jsx ──────────────────────────────────────────────────────────
// // Bottom section: grid of related products
// // ✅ FIX 1: via.placeholder.com → offline-safe inline SVG fallback
// // ✅ FIX 2: Real like_count and views instead of Math.random()
// // ✅ FIX 3: Shows product name and condition badge

// import React from 'react';
// import styles from '../ProductDetailModal.module.css';

// const FALLBACK_IMG_200 =
//   "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' " +
//   "viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E" +
//   "%3Crect x='75' y='55' width='50' height='40' rx='4' fill='%23ccc'/%3E" +
//   "%3Ccircle cx='88' cy='72' r='6' fill='%23aaa'/%3E" +
//   "%3Cpolygon points='75,95 93,75 108,85 123,68 138,95' fill='%23bbb'/%3E" +
//   "%3Ctext x='100' y='130' font-family='Arial' font-size='11' fill='%23999' " +
//   "text-anchor='middle'%3ENo Image%3C/text%3E%3C/svg%3E";

// const onImgError = (e) => {
//   if (e.target.src !== FALLBACK_IMG_200) e.target.src = FALLBACK_IMG_200;
// };

// const SimilarItems = ({ relatedProducts, onProductClick }) => {
//   if (!relatedProducts || relatedProducts.length === 0) return null;

//   return (
//     <div className={styles.similarItemsSection} id="similar-items">
//       <div className={styles.sectionHeader}>
//         <h3>Similar Items</h3>
//         <a href="#" className={styles.seeAllLink}>See All →</a>
//       </div>
//       <div className={styles.similarItemsGrid}>
//         {relatedProducts.slice(0, 4).map((p) => (
//           <div
//             className={styles.similarItemCard}
//             key={p.id}
//             onClick={() => onProductClick(p)}
//           >
//             <div className={styles.similarItemImage}>
//               <img
//                 src={p.img || FALLBACK_IMG_200}
//                 alt={p.name}
//                 onError={onImgError}
//               />
//               {/* ✅ Real like count */}
//               <div className={styles.similarItemLikes}>
//                 <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
//                   <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
//                 </svg>
//                 {p.like_count ?? p.likes ?? 0}
//               </div>
//               {/* Condition badge */}
//               {p.condition && (
//                 <div style={{
//                   position: 'absolute', top: 6, left: 6,
//                   background: 'rgba(0,0,0,0.55)', color: '#fff',
//                   fontSize: '9px', fontWeight: 600, padding: '2px 6px',
//                   borderRadius: '4px', textTransform: 'uppercase',
//                 }}>
//                   {p.condition}
//                 </div>
//               )}
//             </div>
//             <div className={styles.similarItemInfo}>
//               {/* Product name */}
//               <div style={{
//                 fontSize: '11px', fontWeight: 600, color: '#111',
//                 overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
//                 marginBottom: '2px',
//               }}>
//                 {p.name || p.title}
//               </div>
//               <div className={styles.priceSmall}>KES {p.price?.toLocaleString()}</div>
//               {/* ✅ Real view count */}
//               <div className={styles.similarItemViews}>
//                 <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
//                   <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" />
//                   <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
//                 </svg>
//                 {p.views ?? 0}
//               </div>
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };

// export default SimilarItems;



















// // // ── SimilarItems.jsx ──────────────────────────────────────────────────────────
// // // Bottom section: grid of related products
// // // ✅ FIXED: via.placeholder.com → offline-safe inline SVG fallback

// // import React from 'react';
// // import styles from '../ProductDetailModal.module.css';

// // const FALLBACK_IMG_200 =
// //   "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' " +
// //   "viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E" +
// //   "%3Crect x='75' y='55' width='50' height='40' rx='4' fill='%23ccc'/%3E" +
// //   "%3Ccircle cx='88' cy='72' r='6' fill='%23aaa'/%3E" +
// //   "%3Cpolygon points='75,95 93,75 108,85 123,68 138,95' fill='%23bbb'/%3E" +
// //   "%3Ctext x='100' y='130' font-family='Arial' font-size='11' fill='%23999' " +
// //   "text-anchor='middle'%3ENo Image%3C/text%3E%3C/svg%3E";

// // const onImgError = (e) => { if (e.target.src !== FALLBACK_IMG_200) e.target.src = FALLBACK_IMG_200; };

// // const SimilarItems = ({ relatedProducts, onProductClick }) => {
// //   if (!relatedProducts || relatedProducts.length === 0) return null;

// //   return (
// //     <div className={styles.similarItemsSection} id="similar-items">
// //       <div className={styles.sectionHeader}>
// //         <h3>Similar Items</h3>
// //         <a href="#" className={styles.seeAllLink}>See All →</a>
// //       </div>
// //       <div className={styles.similarItemsGrid}>
// //         {relatedProducts.slice(0, 4).map((p) => (
// //           <div
// //             className={styles.similarItemCard}
// //             key={p.id}
// //             onClick={() => onProductClick(p)}
// //           >
// //             <div className={styles.similarItemImage}>
// //               <img
// //                 src={p.img || FALLBACK_IMG_200}
// //                 alt={p.name}
// //                 onError={onImgError}
// //               />
// //               <div className={styles.similarItemLikes}>
// //                 <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
// //                   <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
// //                 </svg>
// //                 {Math.floor(Math.random() * 100)}
// //               </div>
// //             </div>
// //             <div className={styles.similarItemInfo}>
// //               <div className={styles.priceSmall}>KES {p.price.toLocaleString()}</div>
// //               <div className={styles.similarItemViews}>
// //                 <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
// //                   <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" />
// //                   <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
// //                 </svg>
// //                 {Math.floor(Math.random() * 100)}
// //               </div>
// //             </div>
// //           </div>
// //         ))}
// //       </div>
// //     </div>
// //   );
// // };

// // export default SimilarItems;